import type { ChecklistItem } from '@app/collections/collection-inspection/_models/checklist.model';
import type { IInspectionPlan } from '@app/inspection-plan/inspection-plan.interface';

/**
 * The plan rules a hands-free session runs on, kept free of Angular so they can be exercised
 * directly.
 *
 * A periodic plan is one region, one inspection type and a window of calendar days. Nothing on a
 * checklist row names the plan it belongs to: a point's occurrence (its rows on one date) is in the
 * plan when that date falls inside the window — the same rule the server counts a plan's points by.
 * Periodic plans only count rows with no critical area; rows with one belong to action plans.
 *
 * Every date here is a calendar day, `yyyy-mm-dd`, compared as a string. See calendarDay for why a
 * row's day cannot simply be `new Date(row.Date).toDateString()`.
 */

/** The plan a session writes into. */
export interface HandsFreePlan {
  id: string;
  regionId: string;
  inspectionTypeId: string;
  inspectionTypeName: string;
  /** Which cycle of its recurring schedule this plan is — how the schedule page tells plans of one type apart. */
  cycle: number;
  /** The target (scheduled) date. */
  plannedDay: string;
  windowStart: string;
  windowEnd: string;
  executed: number;
  outlined: number;
  skipped: number;
}

/** A point's latest occurrence inside a plan's window: the rows a spoken reading goes into. */
export interface PlanOccurrence {
  day: string;
  items: ChecklistItem[];
  /** The rows that can still take a value. Empty when the occurrence is locked. */
  writable: ChecklistItem[];
}

/** The parts of an inspection point the plan rules read. */
export interface PlanPointLike {
  inspectionTypeId: string;
  regionId?: string;
  regionIds?: string[];
  checkList?: ChecklistItem[];
  elementResult?: { Active?: boolean; RegionIds?: string[]; InsertedRegionId?: string };
}

const UTC_MIDNIGHT = /^(\d{4}-\d{2}-\d{2})T00:00:00(\.0+)?(Z|\+00:00)$/;
const BARE_DAY = /^(\d{4}-\d{2}-\d{2})$/;

/**
 * The calendar day a stored date stands for.
 *
 * Two writers disagree on what a day looks like. The browser saves a day as local midnight, so in
 * Brazil 1 March arrives as `2026-03-01T03:00:00Z`; the server saves one as UTC midnight,
 * `2026-03-01T00:00:00Z` — which `new Date(...)` shows as 28 February anywhere west of Greenwich.
 * A UTC-midnight instant is therefore read by its UTC date and anything else by the local date,
 * which gets both writers right in every time zone.
 */
export function calendarDay(value: Date | string | null | undefined): string | undefined {
  if (value === null || value === undefined || value === '') return undefined;

  if (typeof value === 'string') {
    const match = UTC_MIDNIGHT.exec(value) ?? BARE_DAY.exec(value);
    if (match) return match[1];
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Local midnight of a calendar day — the shape the twin's reference date and the browser's rows use. */
export function dayToDate(day: string): Date {
  const [year, month, date] = day.split('-').map(Number);
  return new Date(year, month - 1, date);
}

/**
 * The hands-free view of a plan, or undefined when it cannot take readings at all: action plans
 * (their rows are tied to critical areas), completed plans, and plans without a usable window.
 * Which inspection types can be read aloud is the caller's call.
 */
export function toHandsFreePlan(plan: IInspectionPlan | null | undefined): HandsFreePlan | undefined {
  if (!plan?.id || plan.isActionPlan || plan.isCompleted || (plan as any).isCluster) return undefined;

  const windowStart = calendarDay(plan.assetWindowStartsAt as any);
  const windowEnd = calendarDay(plan.assetWindowEndsAt as any);
  if (!windowStart || !windowEnd || windowStart > windowEnd) return undefined;

  return {
    id: plan.id,
    regionId: plan.regionId,
    inspectionTypeId: plan.inspectionTypeId,
    inspectionTypeName: plan.inspectionTypeName ?? '',
    cycle: plan.inspectionCycle ?? 0,
    plannedDay: calendarDay(plan.plannedDate as any) ?? windowStart,
    windowStart,
    windowEnd,
    executed: plan.executedElements ?? 0,
    outlined: plan.outlinedElements ?? 0,
    skipped: plan.skippedElements ?? 0,
  };
}

/** "Thickness Gauging · Cycle 3" — plans carry no name of their own, so type and cycle stand in for one. */
export function describePlan(plan: HandsFreePlan): string {
  const type = plan.inspectionTypeName || 'Inspection plan';
  return plan.cycle > 0 ? `${type} · Cycle ${plan.cycle}` : type;
}

/** "1 Jun 2026". */
export function describeDay(day: string): string {
  return dayToDate(day).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "1 Mar – 31 Aug 2026", or with both years when the window spans a new year. */
export function describeWindow(plan: HandsFreePlan): string {
  const start = dayToDate(plan.windowStart);
  const end = dayToDate(plan.windowEnd);
  const sameYear = start.getFullYear() === end.getFullYear();
  const from = start.toLocaleDateString('en-GB', sameYear
    ? { day: 'numeric', month: 'short' }
    : { day: 'numeric', month: 'short', year: 'numeric' });
  const to = end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${from} – ${to}`;
}

export function windowHolds(plan: HandsFreePlan, day: string | undefined): boolean {
  return !!day && day >= plan.windowStart && day <= plan.windowEnd;
}

/**
 * The plan a session opens on when nobody picked one: the one whose window holds the day the twin
 * is showing. That is the plan a reading on that day was already going into, so nothing changes for
 * someone who never looks at the plan. No such plan means the user has to choose.
 */
export function defaultPlan(plans: HandsFreePlan[], referenceDay: string | undefined): HandsFreePlan | undefined {
  const holding = plans.filter(plan => windowHolds(plan, referenceDay));
  return holding.sort((a, b) => distance(a.plannedDay, referenceDay) - distance(b.plannedDay, referenceDay))[0];
}

/**
 * A point's latest occurrence inside the plan's window, or undefined when it has none — which is
 * what "not in this plan" means. Several occurrences can fall inside one window (a point re-read
 * later in the campaign); the latest is the one a new reading belongs to.
 */
export function occurrenceInPlan(checkList: ChecklistItem[] | undefined, plan: HandsFreePlan): PlanOccurrence | undefined {
  let latest: string | undefined;
  const byDay = new Map<string, ChecklistItem[]>();

  for (const item of checkList ?? []) {
    // A row with a critical area belongs to an action plan, never to a periodic one.
    if (!item || item.CriticalAreaId) continue;
    const day = calendarDay(item.Date as any);
    if (!windowHolds(plan, day)) continue;

    const rows = byDay.get(day!) ?? [];
    rows.push(item);
    byDay.set(day!, rows);
    if (!latest || day! > latest) latest = day;
  }

  if (!latest) return undefined;
  const items = byDay.get(latest)!;
  return { day: latest, items, writable: items.filter(item => !item.IsLocked) };
}

/**
 * The point's occurrence in the plan when the point belongs to it: same inspection type, same
 * region, still in service, and something dated inside the window.
 */
export function planOccurrenceOf(point: PlanPointLike, plan: HandsFreePlan): PlanOccurrence | undefined {
  if (point.inspectionTypeId !== plan.inspectionTypeId) return undefined;
  if (point.elementResult?.Active === false) return undefined;
  if (!inRegion(point, plan.regionId)) return undefined;
  return occurrenceInPlan(point.checkList, plan);
}

/** The last real value on these rows — what the point currently reads in this occurrence. */
export function occurrenceValue(items: ChecklistItem[]): number | undefined {
  for (let index = items.length - 1; index >= 0; index--) {
    const raw = items[index]?.Value;
    if (raw === '' || raw === null || raw === undefined) continue;
    const value = Number(raw);
    if (!Number.isNaN(value)) return value;
  }
  return undefined;
}

function inRegion(point: PlanPointLike, regionId: string): boolean {
  if (!regionId) return true;
  return point.regionId === regionId
    || !!point.regionIds?.includes(regionId)
    || !!point.elementResult?.RegionIds?.includes(regionId)
    || point.elementResult?.InsertedRegionId === regionId;
}

function distance(day: string, from: string | undefined): number {
  if (!from) return 0;
  return Math.abs(dayToDate(day).getTime() - dayToDate(from).getTime());
}

function pad(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}
